import { useCallback, type ReactNode, type Ref } from "react";
import { Input as BaseInput } from "@base-ui/react/input";
import { useField, useFormikContext } from "formik";
import type { TextFieldPath } from "@components/forms/fields/TextField";
import type { SlateValue } from "@components/mainInterface/chat/slate/slateTypes";
import { Field } from "@design-system/Field/Field";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerChevron,
    PickerTriggerElement,
} from "@design-system/Picker/Picker.types";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { shouldShowFieldError } from "@helpers/form";
import { TemplatesIcon } from "@icons";
import { DescriptionField } from "./DescriptionField";
import styles from "./TaskDrawerHeaderFields.module.scss";

interface TaskDrawerHeaderFieldsProps<Name extends string> {
    /** Formik path of the task title; must be rendered inside a FormikProvider. */
    titleName: Name;
    titlePlaceholder: string;
    titleRef?: Ref<HTMLInputElement>;
    description: string | null | undefined;
    onDescriptionChange: (value: SlateValue) => void;
    /** Bump to remount the opening chat editor with empty content. */
    descriptionResetKey?: number;
    /**
     * Renders the workflow picker below the description. Receives the
     * header-styled trigger and chevron; spread them onto the picker.
     */
    renderWorkflowPicker?: (props: WorkflowPickerHeaderProps) => ReactNode;
}

interface WorkflowPickerHeaderProps {
    trigger: PickerTriggerElement;
    chevron: PickerChevron;
}

/**
 * Top of every task drawer (create task, close template add/edit task): a
 * borderless title row above the opening chat editor. Owning both here keeps
 * the drawers from drifting apart visually.
 */
export const TaskDrawerHeaderFields = <
    Values,
    Name extends TextFieldPath<Values> = TextFieldPath<Values>,
>({
    titleName,
    titlePlaceholder,
    titleRef,
    description,
    onDescriptionChange,
    descriptionResetKey,
    renderWorkflowPicker,
}: TaskDrawerHeaderFieldsProps<Name>) => {
    const { submitCount } = useFormikContext<Values>();
    const [field, meta] = useField<string | null | undefined>(titleName);
    const isTitleInvalid = shouldShowFieldError({
        error: meta.error,
        touched: meta.touched,
        submitCount,
    });

    const assignTitleRef = useCallback(
        (node: HTMLElement | null) => {
            mergeRefs([titleRef])(
                node instanceof HTMLInputElement ? node : null,
            );
        },
        [titleRef],
    );

    return (
        <div>
            <Field.Root invalid={isTitleInvalid}>
                <BaseInput
                    name={field.name}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={assignTitleRef}
                    placeholder={titlePlaceholder}
                    aria-invalid={isTitleInvalid || undefined}
                    aria-required
                    className={styles.titleInput}
                />
            </Field.Root>
            <DescriptionField
                key={descriptionResetKey}
                onChange={onDescriptionChange}
                serializedValue={description}
            />
            {renderWorkflowPicker && (
                <div className={styles.workflowPicker}>
                    {renderWorkflowPicker({
                        chevron: "none",
                        trigger: (
                            <Picker.Button
                                variant="ghost"
                                leftIcon={<TemplatesIcon />}
                                size="md"
                                block={false}
                                className={styles.workflowTrigger}
                            />
                        ),
                    })}
                </div>
            )}
        </div>
    );
};
